import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  cn,
} from "@nexus/ui";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getOrderStatusVisual } from "../../lib/order-status-visual";
import { getFulfillmentStatusLabel } from "../../lib/fulfillment-status-label";
import type { MarketplaceFulfillmentViewer } from "../../lib/fulfillment-badge-variant";
import {
  STATUS_OPTION_ITEM_CLASS,
  StatusOptionContent,
} from "./StatusOptionContent";

interface StatusMenuProps {
  value: MarketplaceFulfillmentStatus;
  options: readonly MarketplaceFulfillmentStatus[];
  /** Read-only renders the badge alone; no menu, no chevron. */
  editable: boolean;
  pending?: boolean;
  /** Screen-reader label for the trigger — say which script or order it changes. */
  ariaLabel: string;
  /** Menu heading; defaults to "Change status". */
  heading?: string;
  /** Rendered after the option list — e.g. a "+1 more" mixed-status note. */
  trailing?: ReactNode;
  className?: string;
  onChange: (next: MarketplaceFulfillmentStatus) => void;
}

/**
 * The single status control. The trigger *is* the badge so the read and the
 * edit share one element — no separate dropdown duplicating the pill.
 */
export function StatusMenu({
  value,
  options,
  editable,
  pending = false,
  ariaLabel,
  heading,
  trailing,
  className,
  onChange,
}: StatusMenuProps) {
  const { t } = useTranslation();

  if (!editable) {
    return (
      <span
        className={cn("inline-flex min-w-0 items-center gap-1.5", className)}
      >
        <StatusPill status={value} />
        {trailing}
      </span>
    );
  }

  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            aria-busy={pending || undefined}
            disabled={pending}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className="inline-flex max-w-full cursor-pointer items-center rounded-sm outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-progress disabled:opacity-60"
          >
            <StatusPill
              status={value}
              end={
                <Icon
                  name={pending ? "loader" : "chevron-down"}
                  size="xs"
                  color="inherit"
                  className={cn("opacity-70", pending && "animate-spin")}
                  aria-hidden
                />
              }
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="min-w-72"
          onClick={(event) => event.stopPropagation()}
        >
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            {heading ?? t("marketplace.orders.list.changeStatus")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map((option) => {
            const isCurrent = option === value;
            return (
              <DropdownMenuItem
                key={option}
                aria-current={isCurrent ? "true" : undefined}
                className={cn(
                  STATUS_OPTION_ITEM_CLASS,
                  isCurrent && "bg-muted/60",
                )}
                onSelect={() => {
                  if (!isCurrent) onChange(option);
                }}
              >
                <StatusOptionContent status={option} selected={isCurrent} />
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {trailing}
    </span>
  );
}

const STATUS_PILL_FLASH_MS = 900;

export function StatusPill({
  status,
  viewer = "client",
  className,
  end,
}: {
  status: MarketplaceFulfillmentStatus;
  viewer?: MarketplaceFulfillmentViewer;
  className?: string;
  end?: ReactNode;
}) {
  const { t } = useTranslation();
  const visual = getOrderStatusVisual(status);
  const previousStatus = useRef(status);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (previousStatus.current === status) return;
    previousStatus.current = status;
    setFlash(true);
    const timer = window.setTimeout(() => setFlash(false), STATUS_PILL_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] leading-none transition-transform duration-300",
        visual.pillClass,
        flash &&
          "ring-2 ring-ring/70 shadow-[0_0_0_1px_hsl(var(--ring)/0.35)] scale-[1.02]",
        className,
      )}
    >
      <span
        aria-hidden
        className={cn("size-1.5 shrink-0 rounded-full", visual.dotClass)}
      />
      <span className="truncate">
        {getFulfillmentStatusLabel(t, status, viewer)}
      </span>
      {end}
    </span>
  );
}
